import moment from 'moment'

/**
 * Domain state, derived once so the dashboard, the list, and any future detail
 * page all describe a domain the same way.
 *
 * Deliberately separates *lifecycle* (does the registration still exist) from
 * *attachment* (is a shop being served from it). They are independent: a domain
 * can be perfectly healthy and attached to nothing, which is the normal state
 * for someone who bought a name to park it.
 */

export type DomainStatus = 'pending' | 'active' | 'grace' | 'redemption' | 'deleted' | 'canceled'

export type DomainOrder = {
  id: string
  domain: string
  status: DomainStatus
  nginxStatus?: string
  vendorId?: string | null
  vendor?: { id: string, name: string } | null
  attachedAt?: string | null
  period?: number
  registeredAt?: string | null
  graceAt?: string | null
  redemptionAt?: string | null
  createdAt?: string | null
  [key: string]: any
}

/** Domains the registrar still holds for us - everything else is history. */
export const LIVE_STATUSES: DomainStatus[] = ['active', 'grace', 'redemption']

export const isLive = (d: DomainOrder) => LIVE_STATUSES.includes(d.status)

/**
 * Bought but not yet paid for — the registrar holds nothing yet.
 *
 * Deliberately separate from `isLive`, which gates whether a name can be
 * managed. A pending order is neither manageable nor finished: it is the one
 * state where the owner still has something to do, so listing it under "ended"
 * alongside deleted and canceled names buries the only actionable card on the
 * page.
 */
export const isPending = (d: DomainOrder) => d.status === 'pending'

/** Genuinely over: the name is gone and nothing can be done about it. */
export const isEnded = (d: DomainOrder) => !isLive(d) && !isPending(d)

/** Renewal is urgent inside this window before the registration period ends. */
export const EXPIRY_WARNING_DAYS = 30

export type StatusTone = 'success' | 'warning' | 'danger' | 'muted'

/**
 * One label + tone per lifecycle state.
 *
 * Tones never carry meaning alone - every consumer pairs them with this label
 * and an icon, so the four states stay distinguishable without relying on hue.
 */
export function statusMeta(status: DomainStatus): { label: string, tone: StatusTone } {
  switch(status) {
    case 'active':     return { label: 'Идэвхтэй', tone: 'success' }
    case 'pending':    return { label: 'Төлбөр хүлээгдэж буй', tone: 'warning' }
    case 'grace':      return { label: 'Хугацаа дууссан', tone: 'warning' }
    case 'redemption': return { label: 'Сэргээх хугацаа', tone: 'danger' }
    case 'deleted':    return { label: 'Устгагдсан', tone: 'muted' }
    case 'canceled':   return { label: 'Цуцлагдсан', tone: 'muted' }
    default:           return { label: 'Тодорхойгүй', tone: 'muted' }
  }
}

/** Whole days until the registration period ends. Negative once it has passed. */
export function daysUntilExpiry(d: DomainOrder): number | null {
  if(!d.graceAt) return null
  return moment(d.graceAt).startOf('day').diff(moment().startOf('day'), 'days')
}

export function isExpiringSoon(d: DomainOrder): boolean {
  if(d.status !== 'active') return false
  const days = daysUntilExpiry(d)
  return days !== null && days >= 0 && days <= EXPIRY_WARNING_DAYS
}

/** Needs the owner to do something, in priority order. */
export function needsAttention(d: DomainOrder): boolean {
  return d.status === 'grace' || d.status === 'redemption' || isExpiringSoon(d)
}

export const isAttached = (d: DomainOrder) => Boolean(d.vendorId)

/** `YYYY.MM.DD`, matching every other absolute date in the Shop panels. */
export function formatDate(value?: string | null): string {
  if(!value) return '-'
  return moment(value).format('YYYY.MM.DD')
}

/**
 * Splits a domain into its name and TLD so the TLD can be rendered in the accent
 * colour - the one piece of visual signature this product carries, and the same
 * treatment used on the sign-in screen.
 */
export function splitDomain(domain: string): { name: string, tld: string } {
  const i = domain.indexOf('.')
  if(i < 0) return { name: domain, tld: '' }
  return { name: domain.slice(0, i), tld: domain.slice(i) }
}

/**
 * How far through its paid term a domain is, as a percentage plus the days left.
 *
 * A domain is a lease, not a possession, so the term itself is the fact worth
 * drawing. Measured from registration to the day the grace period opens, which
 * is the real end of what was paid for.
 *
 * `registeredAt` is missing on older records, so the start falls back to
 * `period` years counted back from `graceAt`. That keeps every card able to draw
 * a bar instead of leaving holes in a grid.
 */
export function renewalProgress(d: DomainOrder): { percent: number, remaining: number, days: number } | null {
  if(!d.graceAt) return null

  const end = moment(d.graceAt).startOf('day')
  const start = d.registeredAt
    ? moment(d.registeredAt).startOf('day')
    : end.clone().subtract(Math.max(1, Number(d.period) || 1), 'years')

  const total = end.diff(start, 'days')
  if(total <= 0) return null

  const days = end.diff(moment().startOf('day'), 'days')
  const elapsed = total - days

  const clamp = (n: number) => Math.min(100, Math.max(0, Math.round(n)))

  return {
    /** Share of the term USED UP. Grows toward 100 as expiry approaches. */
    percent: clamp((elapsed / total) * 100),
    /** Share of the term LEFT — what the card's bar draws, so a fresh
     *  registration reads as full and drains as the year is spent.
     *
     *  Floored at 1 while any day remains: one day of a year is 0.27%, which
     *  Math.round takes to 0, making "expires tomorrow" identical to "already
     *  expired". Those two must never render the same. */
    remaining: days > 0 ? Math.max(1, clamp((days / total) * 100)) : 0,
    days,
  }
}

/** Live domains, urgent ones first, then alphabetical. */
export function sortByUrgency(domains: DomainOrder[]): DomainOrder[] {
  return [...domains].sort((a, b) => {
    const urgency = Number(needsAttention(b)) - Number(needsAttention(a))
    if(urgency !== 0) return urgency
    return String(a.domain).localeCompare(String(b.domain))
  })
}

/* ---------------------------------------------------------------- DNS ---- */

export type DnsRecord = {
  type: string
  host: string
  value: string
  ttl: string
  status?: string
  priority?: string
  /** SRV only. Read back so editing an SRV does not silently zero them. */
  port?: string
  weight?: string
}

/**
 * Record types this tab manages.
 *
 * NS is deliberately absent. Apex NS records ARE the registry delegation, which
 * the Нэрийн сервер tab owns through a different endpoint (modify-ns) — listing
 * them here would show the same four rows twice and offer edit/delete buttons
 * that fight the tab actually responsible for them. The backend still supports
 * NS (see LB_DNS_TYPE_NAMES); this is a UI scope decision, not a capability one.
 */
export const DNS_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV'] as const

/**
 * Types hidden from the DNS tab because another screen owns them.
 *
 * Filtered at parse time rather than at render: a type that must not be managed
 * here must also not appear in the filter chips, the counts, or the search
 * results, and doing it once at the source guarantees all four agree.
 */
const DNS_HIDDEN_TYPES = ['NS']

/**
 * Flattens LogicBoxes' DNS payload into a plain list.
 *
 * The API returns one object per record type, keyed by stringified index and
 * mixed in with `recsonpage` / `recsindb` counters - so entries have to be
 * filtered by shape rather than by key. It also names the TTL field
 * `timetolive` on reads while every write expects `ttl`; normalising here keeps
 * that asymmetry in one place instead of in every component.
 */
export function parseDnsRecords(dns: any): DnsRecord[] {
  if(!dns || typeof dns !== 'object') return []

  const records: DnsRecord[] = []
  for(const type of Object.keys(dns)) {
    const group = dns[type]
    if(!group || typeof group !== 'object') continue

    for(const entry of Object.values<any>(group)) {
      if(!entry || typeof entry !== 'object' || !entry.type) continue
      records.push({
        type: entry.type,
        host: entry.host || '',
        value: entry.value || '',
        ttl: String(entry.timetolive || entry.ttl || ''),
        status: entry.status,
        priority: entry.priority !== undefined ? String(entry.priority) : undefined,
        port: entry.port !== undefined ? String(entry.port) : undefined,
        weight: entry.weight !== undefined ? String(entry.weight) : undefined,
      })
    }
  }
  return records.filter(r => !DNS_HIDDEN_TYPES.includes(r.type))
}

/** Groups parsed records by type, preserving DNS_TYPES order. */
export function groupDnsRecords(records: DnsRecord[]): Array<{ type: string, records: DnsRecord[] }> {
  const known = DNS_TYPES.map(type => ({ type, records: records.filter(r => r.type === type) }))
  const extra = records.filter(r => !DNS_TYPES.includes(r.type as any))
  const extraTypes = [...new Set(extra.map(r => r.type))]
    .map(type => ({ type, records: extra.filter(r => r.type === type) }))
  return [...known, ...extraTypes].filter(g => g.records.length > 0)
}

/**
 * What each record type is for, in one place.
 *
 * The colour is a token name, not a raw class: record type is the one axis in
 * this table worth colouring, because it is how people locate the row they came
 * for ("where is my MX?"). Everything else stays neutral so the accent means
 * something.
 *
 * `hint` is shown under the type in the add/edit form. DNS is the part of
 * owning a domain most people are least sure about, and a one-line answer at
 * the point of choosing is worth more than a docs link they will not open.
 */
export const DNS_TYPE_META: Record<string, { token: string, label: string, hint: string }> = {
  A: {
    token: 'blue',
    label: 'IP хаяг руу чиглүүлнэ',
    hint: 'Домэйнийг сервэрийн IPv4 хаяг руу заана. Жишээ нь 202.131.1.6.',
  },
  CNAME: {
    token: 'purple',
    label: 'Өөр домэйн руу чиглүүлнэ',
    hint: 'Дэд домэйнийг өөр домэйн нэр рүү заана. Жишээ нь www → shop.mn.',
  },
  MX: {
    token: 'warning',
    label: 'Имэйл сервер',
    hint: 'Тухайн домэйн руу илгээсэн имэйл хаашаа очихыг заана.',
  },
  TXT: {
    token: 'muted',
    label: 'Текст бичлэг',
    hint: 'Баталгаажуулалт болон SPF зэрэг тохиргоог текстээр хадгална.',
  },
  AAAA: {
    token: 'blue',
    label: 'IPv6 хаяг руу чиглүүлнэ',
    hint: 'A бичлэгтэй адил боловч IPv6 хаяг заана. Жишээ нь 2001:db8::1.',
  },
  SRV: {
    token: 'rose',
    label: 'Үйлчилгээний бичлэг',
    hint: 'Тодорхой үйлчилгээ ямар сервер, ямар порт дээр ажиллахыг заана.',
  },
}

/** SRV carries port and weight on top of priority; nothing else does. */
export function dnsTypeNeedsPriority(type: string) {
  return type === 'MX' || type === 'SRV'
}

export function dnsTypeNeedsSrvFields(type: string) {
  return type === 'SRV'
}

export function dnsTypeMeta(type: string) {
  return DNS_TYPE_META[type] || { token: 'muted', label: 'Бусад бичлэг', hint: '' }
}

/**
 * Free-text filter across the fields someone would actually search by.
 *
 * Matches host, value and type together rather than one chosen field: the
 * person searching "202.131" is looking for a value and the person searching
 * "www" is looking for a host, and making them pick which first is a question
 * the UI can answer itself. `@` is matched against the apex explicitly, since
 * the empty host renders as `@` but is not stored that way.
 */
export function filterDnsRecords(records: DnsRecord[], query: string, type: string = 'all'): DnsRecord[] {
  const q = query.trim().toLowerCase()
  return records.filter(record => {
    if(type !== 'all' && record.type !== type) return false
    if(!q) return true
    const host = (record.host || '@').toLowerCase()
    return host.includes(q)
      || record.value.toLowerCase().includes(q)
      || record.type.toLowerCase().includes(q)
  })
}

/** Record types actually present, for the filter chips. Ordered by DNS_TYPES. */
export function dnsTypeCounts(records: DnsRecord[]): Array<{ type: string, count: number }> {
  const seen = [...new Set(records.map(r => r.type))]
  const known = DNS_TYPES.filter(t => seen.includes(t))
  const extra = seen.filter(t => !DNS_TYPES.includes(t as any)).sort()
  return [...known, ...extra].map(type => ({ type, count: records.filter(r => r.type === type).length }))
}

/* ---------- Invoices ---------- */

export type DomainInvoiceStatus = 'pending' | 'paid' | 'canceled' | 'refunded' | 'failed'

export type DomainInvoice = {
  id: string
  customId: string
  type?: string
  status: DomainInvoiceStatus
  totalAmount: number
  amount?: number
  saleAmount?: number | null
  period?: number
  method?: string | null
  paidAt?: string | null
  createdAt?: string | null
  expireAt?: string | null
  [key: string]: any
}

export function invoiceStatusMeta(status: DomainInvoiceStatus): { label: string, tone: StatusTone } {
  switch(status) {
    case 'paid':     return { label: 'Төлөгдсөн', tone: 'success' }
    case 'pending':  return { label: 'Хүлээгдэж буй', tone: 'warning' }
    case 'canceled': return { label: 'Цуцлагдсан', tone: 'muted' }
    case 'refunded': return { label: 'Буцаагдсан', tone: 'muted' }
    case 'failed':   return { label: 'Амжилтгүй', tone: 'danger' }
    default:         return { label: 'Тодорхойгүй', tone: 'muted' }
  }
}

/** What the payment was for. Registration and renewal are the only common ones. */
export function invoiceTypeLabel(type?: string): string {
  switch(type) {
    case 'registration': return 'Бүртгэл'
    case 'renewal':      return 'Сунгалт'
    case 'transfer':     return 'Шилжүүлэг'
    case 'privacy':      return 'Нууцлал'
    default:             return 'Бусад'
  }
}

export function paymentMethodLabel(method?: string | null): string {
  switch(method) {
    case 'qpay':     return 'QPay'
    case 'transfer': return 'Шилжүүлэг'
    case 'storepay': return 'StorePay'
    case 'pocket':   return 'Pocket'
    case 'omniway':  return 'OmniWay'
    default:         return ''
  }
}

/**
 * Newest first, and pending invoices hoisted above everything else.
 *
 * An unpaid invoice is the only row here that is a task rather than a record —
 * it is the one the owner may still need to act on, so it does not get buried
 * under years of paid history just because it happens to be older.
 */
export function sortDomainInvoices(invoices: DomainInvoice[]): DomainInvoice[] {
  return [...invoices].sort((a, b) => {
    const aPending = a.status === 'pending' ? 0 : 1
    const bPending = b.status === 'pending' ? 0 : 1
    if(aPending !== bPending) return aPending - bPending
    return moment(b.createdAt || 0).valueOf() - moment(a.createdAt || 0).valueOf()
  })
}

/** Total actually paid across this domain's life — renewals included. */
export function totalPaid(invoices: DomainInvoice[]): number {
  return invoices
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0)
}
